/**
 * Last wishes. Funeral, organ donation, digital disposal, memorial, pet,
 * charity, personal messages. Priority + audience tagging.
 */
import { store } from '../store.ts';
import { withVault, injectCommonStyles, escapeHtml } from './_shared.ts';
import { newId, WISH_CATEGORIES, WISH_PRIORITIES } from '../types.ts';
import type { LastWish, WishCategory, WishId, WishPriority } from '../types.ts';

const TPL = /* html */ `
<style>
.priority-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
.priority-chip {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 99px;
  cursor: pointer;
  background: var(--parchment);
  border: 1px solid var(--rule-strong);
  color: var(--ink-dim);
  user-select: none;
}
.priority-chip.must.active { background: var(--crimson); color: var(--parchment); border-color: var(--crimson); }
.priority-chip.preferred.active { background: var(--indigo); color: var(--parchment); border-color: var(--indigo); }
.priority-chip.nice_to_have.active { background: var(--saffron); color: var(--ink); border-color: var(--saffron-deep); }
.wish-body { font-family: var(--display); font-style: italic; color: var(--ink); margin-top: 4px; line-height: 1.4; }
</style>
<section class="frame">
  <h2>Last wishes <span class="deva">अंतिम इच्छाएँ</span></h2>
  <div class="kicker">The decisions a family should not have to make alone</div>
  <div class="list" id="list"></div>
  <form class="form-grid" id="form">
    <label class="field">Category
      <select id="cat" required>
        ${WISH_CATEGORIES.map((c) => `<option value="${c}">${c.replace(/_/g, ' ')}</option>`).join('')}
      </select>
    </label>
    <label class="field">Instruction
      <textarea id="instr" rows="3" required placeholder="e.g. cremation at Lodhi Road, no rituals beyond pind-daan, ashes to Haridwar with Pa"></textarea>
    </label>
    <div class="priority-row" id="prio">
      ${WISH_PRIORITIES.map((p, i) => `<span class="priority-chip ${p} ${i === 1 ? 'active' : ''}" data-p="${p}">${p.replace(/_/g, ' ')}</span>`).join('')}
    </div>
    <div class="btn-row">
      <button type="submit" class="btn">Record wish</button>
      <button type="reset" class="btn ghost">Reset</button>
    </div>
  </form>
</section>
`;

export class WasiyatLastWishes extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub: (() => void) | null = null;
  #priority: WishPriority = 'preferred';

  connectedCallback(): void {
    injectCommonStyles(this.#shadow);
    this.#shadow.innerHTML += TPL;
    this.#unsub = withVault(this, (v, root) => {
      const list = root.getElementById('list');
      if (!list) return;
      if (v.last_wishes.length === 0) {
        list.innerHTML = `<div class="list-empty">No wishes recorded yet.</div>`;
        return;
      }
      const ordered = [...v.last_wishes].sort((a, b) => {
        const o = { must: 0, preferred: 1, nice_to_have: 2 } as const;
        return o[a.priority] - o[b.priority];
      });
      list.innerHTML = ordered.map((w) => {
        const cls = w.priority === 'must' ? 'crimson' : w.priority === 'nice_to_have' ? 'leaf' : '';
        return `<div class="list-row ${cls}">
          <div>
            <div class="title">${escapeHtml(w.category.replace(/_/g, ' '))} <span class="pill ${cls}">${w.priority.replace(/_/g, ' ')}</span></div>
            <div class="wish-body">${escapeHtml(w.instruction)}</div>
          </div>
          <button class="btn tiny ghost" data-del="${w.id}">Remove</button>
        </div>`;
      }).join('');
      list.querySelectorAll<HTMLButtonElement>('button[data-del]').forEach((b) => {
        b.addEventListener('click', () => {
          const id = b.dataset['del'] as WishId;
          void store.mutate((d) => { d.last_wishes = d.last_wishes.filter((x) => x.id !== id); });
        });
      });
    });

    const prio = this.#shadow.getElementById('prio');
    prio?.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (!t.classList.contains('priority-chip')) return;
      prio.querySelectorAll('.priority-chip').forEach((c) => c.classList.remove('active'));
      t.classList.add('active');
      this.#priority = t.dataset['p'] as WishPriority;
    });

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const cat = (this.#shadow.getElementById('cat') as HTMLSelectElement).value as WishCategory;
      const instr = (this.#shadow.getElementById('instr') as HTMLTextAreaElement).value.trim();
      if (!instr) return;
      const w: LastWish = {
        id: newId<WishId>(),
        category: cat,
        instruction: instr,
        priority: this.#priority,
        audience_nominee_ids: [],
        created_at: new Date().toISOString(),
      };
      void store.mutate((d) => { d.last_wishes.push(w); });
      form.reset();
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }
}

customElements.define('wasiyat-last-wishes', WasiyatLastWishes);
